import { useState, useCallback } from "react";
import { Check, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

function ProductImageCarousel({ images, productName, productId }) {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  const onApiSet = useCallback((emblaApi) => {
    setApi(emblaApi);
    emblaApi.on("select", () => setCurrent(emblaApi.selectedScrollSnap()));
  }, []);

  const scrollPrev = () => api?.scrollPrev();
  const scrollNext = () => api?.scrollNext();

  return (
    <div className="relative w-full h-full min-h-[400px] rounded-xl overflow-hidden bg-[#EDE9E1] group/carousel">
      <Carousel setApi={onApiSet} opts={{ loop: true }} className="h-full">
        <CarouselContent className="h-full -ml-0">
          {images.map((src, i) => (
            <CarouselItem key={i} className="h-full pl-0">
              <img
                src={src}
                alt={`${productName} — view ${i + 1}`}
                data-testid={i === 0 ? `product-image-${productId}` : undefined}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Prev / Next arrows */}
      <button
        onClick={scrollPrev}
        aria-label="Previous image"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200 hover:bg-white"
      >
        <ChevronLeft size={18} className="text-[#040404]" />
      </button>
      <button
        onClick={scrollNext}
        aria-label="Next image"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200 hover:bg-white"
      >
        <ChevronRight size={18} className="text-[#040404]" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => api?.scrollTo(i)}
            aria-label={`Go to image ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-200 ${i === current ? "bg-white w-4" : "bg-white/50 w-1.5"
              }`}
          />
        ))}
      </div>
    </div>
  );
}

function ProductText({ product }) {
  return (
    <div className="flex flex-col justify-center h-full bg-white border border-[#BEBEBD]/50 rounded-xl p-8 md:p-10 shadow-[0_4px_24px_rgba(4,4,4,0.06)]">
      <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#A07570]">
        {product.tagline}
      </span>
      <h3
        data-testid={`product-name-${product.id}`}
        className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#040404] mt-3 leading-tight"
      >
        {product.name}
      </h3>
            <p
              className="text-base text-[#585857] mt-4 leading-relaxed [&_strong]:font-bold [&_strong]:text-[#A7332B]"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />

      {/* Benefits */}
      <div className="mt-8">
        <h4 className="text-sm uppercase tracking-[0.15em] font-bold text-[#040404] mb-4">
          Key Benefits
        </h4>
        <ul className="space-y-2.5">
          {product.benefits.map((benefit, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check size={16} className="text-[#A7332B] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#585857]">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Competitive Advantages */}
      <div className="mt-8 bg-[#A7332B]/5 rounded-lg p-6">
        <h4 className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] font-bold text-[#A7332B] mb-4">
          <Zap size={14} />
          Why We're Better
        </h4>
        <ul className="space-y-2.5">
          {product.competitive_advantages.map((adv, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A7332B] mt-2 flex-shrink-0" />
              <span className="text-sm font-medium text-[#040404]">{adv}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function ProductsSection({ products }) {
  if (!products || products.length === 0) return null;

  return (
    <section
      id="products"
      data-testid="products-section"
      className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto"
    >
      {/* Section Header */}
      <div className="mb-20 text-center">
        <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#A07570]">
          Our Products
        </span>
        <h2
          data-testid="products-heading"
          className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#040404] mt-3"
        >
          Engineered for the
          <br />
          textile industry
        </h2>
        <p className="text-base md:text-lg font-medium text-[#585857] leading-relaxed mt-4 max-w-xl mx-auto">
          Every product is designed with decades of manufacturing expertise,
          built to outperform and outlast the competition.
        </p>
      </div>

      {/* Alternating product rows */}
      <div className="flex flex-col gap-24 md:gap-32">
        {products.map((product, index) => {
          const isEven = index % 2 === 0;
          return (
            <article
              key={product.id}
              data-testid={`product-card-${product.id}`}
              className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-stretch"
            >
              {/* On mobile always show image first; on desktop alternate */}
              <div className={isEven ? "md:order-2" : "md:order-1"}>
                <ProductImageCarousel
                  images={product.images}
                  productName={product.name}
                  productId={product.id}
                />
              </div>
              <div className={isEven ? "md:order-1" : "md:order-2"}>
                <ProductText product={product} />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
